import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { AvatarGenerator } from "random-avatar-generator";
import { PlayerInfo, TeamId } from "../interface/GameTypes";
import { ReactionMenu } from "./ReactionMenu";
import { ReactionOverlay, ReactionEvent } from "./ReactionOverlay";
import { ConfettiCelebration } from "./ConfettiCelebration";
import { TrailingShuffleDealIntro } from "./TrailingShuffleDealIntro";
import { DealIntroWaitingTauntPanel } from "./DealIntroWaitingTauntPanel";

const generator = new AvatarGenerator();

const FAKE_PLAYERS = [
  { uuid: "demo-p1", name: "Alice", team: "A" as const },
  { uuid: "demo-p2", name: "Bob", team: "B" as const },
  { uuid: "demo-p3", name: "Charlie", team: "A" as const },
  { uuid: "demo-p4", name: "Diana", team: "B" as const },
];

const DEMO_PLAYERS_INFO: PlayerInfo[] = FAKE_PLAYERS.map((p, seatIndex) => ({
  uuid: p.uuid,
  name: p.name,
  seatIndex,
  teamId: p.team === "A" ? TeamId.A : TeamId.B,
}));

const TEAM_COLORS = { A: "#4caf50", B: "#ffc107" };

interface MenuTarget {
  name: string;
  anchorX: number;
  anchorY: number;
}

type RitualPreview = null | "trailing" | "waiting";

export const DemoReactionsPage = () => {
  const [menuTarget, setMenuTarget] = useState<MenuTarget | null>(null);
  const [reactions, setReactions] = useState<ReactionEvent[]>([]);
  const [confettiTrigger, setConfettiTrigger] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [ritualPreview, setRitualPreview] = useState<RitualPreview>(null);
  const [ritualTaps, setRitualTaps] = useState(0);
  const [ritualLayoutKey, setRitualLayoutKey] = useState(0);

  const closeRitualPreview = useCallback(() => {
    setRitualPreview(null);
    setRitualTaps(0);
  }, []);

  const openTrailingRitual = useCallback(() => {
    setRitualTaps(0);
    setRitualLayoutKey((k) => k + 1);
    setRitualPreview("trailing");
  }, []);

  const handleAvatarClick = (name: string, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuTarget({
      name,
      anchorX: rect.left + rect.width / 2,
      anchorY: rect.top,
    });
  };

  const closeMenu = useCallback(() => setMenuTarget(null), []);

  const handleReactionSelect = useCallback(
    (reactionId: string) => {
      const targetName = menuTarget?.name ?? "Someone";
      const eventId = `demo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setReactions((prev) => [...prev, { eventId, fromPlayerName: targetName, reactionType: reactionId }]);
      setMenuTarget(null);

      setCooldown(1500);
      setTimeout(() => setCooldown(0), 1500);
    },
    [menuTarget?.name]
  );

  const handleReactionComplete = useCallback((eventId: string) => {
    setReactions((prev) => prev.filter((r) => r.eventId !== eventId));
  }, []);

  const sendWaitingDemoReaction = useCallback(
    (reactionId: string) => {
      if (cooldown > 0) return;
      const eventId = `demo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setReactions((prev) => [...prev, { eventId, fromPlayerName: "You (demo)", reactionType: reactionId }]);
      setCooldown(1500);
      window.setTimeout(() => setCooldown(0), 1500);
    },
    [cooldown]
  );

  const fireConfetti = () => {
    setConfettiTrigger(false);
    requestAnimationFrame(() => setConfettiTrigger(true));
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      px: 2,
    }}>
      {ritualPreview === "trailing" && (
        <TrailingShuffleDealIntro
          key={ritualLayoutKey}
          tapsDone={ritualTaps}
          disabled={ritualTaps >= 3}
          onCommitTap={() => setRitualTaps((t) => Math.min(3, t + 1))}
          deadlineMs={Date.now() + 15 * 60 * 1000}
          onDismiss={closeRitualPreview}
        />
      )}

      {ritualPreview === "waiting" && (
        <>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1195,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.78)",
              px: 3,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                maxWidth: 400,
                textAlign: "center",
                p: 3,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.12)",
                bgcolor: "rgba(26,26,46,0.95)",
                pointerEvents: "auto",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1.5, fontSize: "1.05rem" }}>
                Waiting on the trailing team
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.88rem", lineHeight: 1.5, mb: 1 }}>
                Team B is finishing the shuffle ritual. The strip below matches the live ranked table: pick a reaction
                and everyone sees it (here it plays locally on this page).
              </Typography>
              <DealIntroWaitingTauntPanel
                trailingTeam={TeamId.B}
                players={DEMO_PLAYERS_INFO}
                cooldownMs={cooldown}
                onPickReaction={sendWaitingDemoReaction}
              />
              <Button variant="contained" color="secondary" fullWidth onClick={closeRitualPreview} sx={{ py: 1.25, fontWeight: 700, mt: 2 }}>
                Dismiss preview
              </Button>
            </Box>
          </Box>
          <IconButton
            onClick={closeRitualPreview}
            aria-label="Close waiting preview"
            sx={{
              position: "fixed",
              top: "max(10px, env(safe-area-inset-top))",
              right: "max(10px, env(safe-area-inset-right))",
              zIndex: 1300,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.25)",
              minWidth: 48,
              minHeight: 48,
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </>
      )}

      <ConfettiCelebration trigger={confettiTrigger} />
      <ReactionOverlay reactions={reactions} onComplete={handleReactionComplete} />

      {menuTarget && (
        <ReactionMenu
          anchorX={menuTarget.anchorX}
          anchorY={menuTarget.anchorY}
          targetName={menuTarget.name}
          cooldownMs={cooldown}
          onSelect={handleReactionSelect}
          onClose={closeMenu}
        />
      )}

      <Typography variant="h4" sx={{ color: "white", fontWeight: "bold" }}>
        /demo — UI previews
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: -2, textAlign: "center", maxWidth: 520 }}>
        Reactions: use the waiting-screen strip during the shuffle, or click an avatar and pick a throw. Ranked match:
        preview the trailing-team ritual (local only) or the waiting overlay for the other team.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: 560,
        }}
      >
        <Button variant="outlined" color="secondary" onClick={openTrailingRitual} sx={{ fontWeight: 700 }}>
          Preview trailing shuffle ritual
        </Button>
        <Button variant="outlined" onClick={() => setRitualPreview("waiting")} sx={{ fontWeight: 700, color: "rgba(255,255,255,0.9)", borderColor: "rgba(255,255,255,0.35)" }}>
          Preview “other team” waiting screen
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
        {FAKE_PLAYERS.map((p) => (
          <Box
            key={p.uuid}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => handleAvatarClick(p.name, e)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              transition: "transform 0.15s",
              "&:hover": { transform: "scale(1.08)" },
            }}
          >
            <Box
              component="img"
              src={generator.generateRandomAvatar(p.uuid)}
              alt={p.name}
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: `3px solid ${TEAM_COLORS[p.team]}`,
                boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "white", mt: 0.5, fontWeight: "bold" }}
            >
              {p.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: TEAM_COLORS[p.team], fontSize: "0.6rem" }}
            >
              Team {p.team}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button
        variant="contained"
        onClick={fireConfetti}
        sx={{
          mt: 2,
          fontWeight: "bold",
          fontSize: "1rem",
          px: 4,
          py: 1.5,
          background: "linear-gradient(135deg, #ffc107 0%, #ff5722 100%)",
          "&:hover": { background: "linear-gradient(135deg, #ffca28 0%, #ff7043 100%)" },
        }}
      >
        Fire Confetti
      </Button>

      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", mt: 2 }}>
        Navigate to / to return to the game
      </Typography>
    </Box>
  );
};
